import { CopilotPanelContext } from 'models/copilot/copilotBridge';
import { AdminConsentParams } from 'services/Auth/adminConsentParams';

// Where a copilot-started admin consent continues once it returns: the scene the SharePoint card was opened
// on and the copilot context that scene registers. It travels in the consent state suffix - the same
// "location hint" mechanism the integration page uses - because the consent flow itself can only return to a
// registered redirect URI (the library route), never to the scene the user was on.
export type CopilotConsentReturn = {
  context: CopilotPanelContext;
  route: string;
};

const suffixPrefix = 'copilot';
// Unreserved in a URL and absent from the base64url alphabet, so the suffix survives the round trip through
// the identity provider byte for byte and the stored state still equals the returned one.
const suffixSeparator = '.';

const toBase64Url = (value: string): string => {
  let binary = '';
  new TextEncoder().encode(value).forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const fromBase64Url = (value: string): string | undefined => {
  try {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const bytes = Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));

    return new TextDecoder().decode(bytes);
  } catch {
    return undefined;
  }
};

export const getCurrentRoute = (): string => {
  return `${window.location.pathname}${window.location.search}`;
};

export const getCopilotConsentStateSuffix = (context: CopilotPanelContext, route: string): string => {
  return [suffixPrefix, context, toBase64Url(route)].join(suffixSeparator);
};

export const getCopilotConsentReturnFromState = (state: string | undefined): CopilotConsentReturn | undefined => {
  const suffix = state?.split('@')[1];
  if (!suffix) return undefined;

  const parts = suffix.split(suffixSeparator);
  if (parts.length !== 3 || parts[0] !== suffixPrefix) return undefined;

  const context = Number(parts[1]);
  if (!Number.isInteger(context) || CopilotPanelContext[context] === undefined) return undefined;

  const route = fromBase64Url(parts[2]);
  // In-app path only: anything that could leave the app, or is not a path at all, is ignored.
  if (!route || !route.startsWith('/') || route.startsWith('//')) return undefined;

  return { context: context as CopilotPanelContext, route };
};

// The return hint carried by the consent response in the current URL, accepted only when its state is the one
// this browser stored when it started the flow (the check the shared handler applies as well). Read it BEFORE
// handleConsentResponse runs: that handler consumes the stored params.
export const readCopilotConsentReturn = (): CopilotConsentReturn | undefined => {
  const state = new URLSearchParams(window.location.search).get(AdminConsentParams.paramState) ?? undefined;
  const stored = AdminConsentParams.getFromLocalStorage();
  if (!state || stored?.state !== state) return undefined;

  return getCopilotConsentReturnFromState(state);
};
